// src/stores/tabs.ts
import { defineStore } from 'pinia'
import router from '@/routers'
import piniaPersistConfig from '@/config/piniaPersist'

interface Meta {
  title: string
  affix: boolean
  icon: string
}

export interface Tab {
  name: string
  fullPath: string
  path: string
  is_active: boolean
  meta: Meta
}

const defaultTab: Tab = {
  name: 'Dashboard',
  fullPath: '/',
  path: '/',
  is_active: true,
  meta: {
    title: '工作台',
    affix: true,
    icon: ''
  }
}

export const useNavTabStore = defineStore('nav_tabs', {
  state: () => {
    return {
      tabs: [defaultTab] as Tab[]
    }
  },

  getters: {
    getNavTabs(state): Tab[] {
      return state.tabs
    }
  },

  actions: {
    addTabs(TabItem: Tab): void {
      if (this.tabs.length >= 40) {
        // 放宽最大数量或根据需要调整
        console.warn('最多添加 40 个 tab 标签')
        return
      }

      let isExist = false

      this.tabs = this.tabs.map((t) => {
        if (t.name === TabItem.name) {
          isExist = true
          return { ...t, is_active: true }
        } else {
          return { ...t, is_active: false }
        }
      })

      if (!isExist) {
        // 新增时所有其他设为非激活
        this.tabs = this.tabs.map((t) => ({ ...t, is_active: false }))
        this.tabs.push({ ...TabItem, is_active: true })
      }
    },

    getActiveTabIndex(): number | null {
      for (let i = 0; i < this.tabs.length; i++) {
        if (this.tabs[i].is_active) {
          return i
        }
      }
      return null
    },

    selectTab(tab: Tab): void {
      this.tabs = this.tabs.map((t) => (t.name === tab.name ? { ...t, is_active: true } : { ...t, is_active: false }))
      // push 路由（如果 fullPath 存在）
      if (tab.fullPath) {
        router.push(tab.fullPath).catch(() => {})
      }
    },
    removeTab(index: number): void {
      // 防止越界
      if (index < 0 || index >= this.tabs.length) return

      // 如果被删除的是 affix（固定），直接返回
      const target = this.tabs[index]
      if (target.meta?.affix) return

      const activeIndex = this.getActiveTabIndex()
      // remove it
      // const removed = this.tabs.splice(index, 1)[0];
      this.tabs.splice(index, 1)

      // 如果移除的是当前激活项，需要定位到上一个（index-1）或下一个（index）
      if (activeIndex === index) {
        this.tabs.splice(index, 1)
        // 优先上一个，如果没有上一个则取当前 index（因为已经被删除，index 指向原来的下一个）
        const prev = this.tabs[index - 1] ?? this.tabs[index]
        if (prev) {
          prev.is_active = true
          router.push(prev.fullPath).catch(() => {})
        } else {
          // 没有 tabs，退回 dashboard
          router.push('/dashboard').catch(() => {})
          this.tabs = [defaultTab]
        }
      } else {
        // 如果删除非激活项，不改变当前激活项，但确保路由不会丢失
        const current = this.tabs.find((t) => t.is_active)
        // 防止没有激活项
        if (!current) {
          // fallback
          const fallback = this.tabs[0] ?? defaultTab
          fallback.is_active = true
          router.push(fallback.fullPath).catch(() => {})
        }
      }
    },

    // 右键 / 命令用的方法
    refreshCurrentTab() {
      const index = this.getActiveTabIndex()
      if (index != null) {
        // 用 replace 强制刷新当前路由页面
        router.replace({ path: this.tabs[index].fullPath }).catch(() => {})
      }
    },

    removeCurrentTab() {
      const index = this.getActiveTabIndex()
      if (index != null) {
        if (this.tabs[index].meta.affix) {
          return
        }
        this.removeTab(index)
      }
    },

    removeAllTabs() {
      this.tabs = [defaultTab]
      router.push('/dashboard').catch(() => {})
    },

    removeOtherTabs() {
      const index = this.getActiveTabIndex()
      if (index != null) {
        const keepIndex = index
        const keepTab = this.tabs[keepIndex]
        // 保留第一个（defaultTab）和当前页
        this.tabs = [this.tabs[0], keepTab].filter((v, i) => !!v && (i === 0 || v.name === keepTab.name))
        // 保证唯一性和激活
        this.tabs = this.tabs.map((t) => ({ ...t, is_active: t.name === keepTab.name }))
        router.push(keepTab.fullPath).catch(() => {})
      }
    },

    // =========== 新增：重排 tabs 顺序（drag & drop 后调用） ===========
    reorderTabs(newTabs: Tab[]) {
      if (!Array.isArray(newTabs) || newTabs.length === 0) return
      // 这里我们完全用 newTabs 的顺序来替换，但保留 current is_active 状态（如果 newTabs 来自 UI，一般会包含 is_active）
      // 以防 newTabs 来自外部，只保留同名 tab 的 is_active 状态
      const activeName = this.tabs.find((t) => t.is_active)?.name
      // 创建映射，优先用 newTabs 内的内容，但考虑如果 newTabs 没有 meta（极端），fallback 用原来数据
      const oldMap = new Map(this.tabs.map((t) => [t.name, t]))
      this.tabs = newTabs.map((nt) => {
        const base = oldMap.get(nt.name) ?? nt
        return {
          ...base,
          ...nt,
          is_active: nt.is_active ?? nt.name === activeName
        }
      })
      // 确保至少一个 active
      if (!this.tabs.some((t) => t.is_active)) {
        this.tabs[0].is_active = true
      }
    }
  },
  /**
   * ⚠️ 关键点：只存「无 component 的字段」
   */
  persist: piniaPersistConfig(import.meta.env.VITE_META_CODE + 'nav_tabs')
})
